import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { __t } from '../../i18n';
import { Button, Icon, IconButton, Text } from '../../styles';
import Tooltip from '../../Tooltip';
import { IconPlus, IconMinus } from 'common/icons';
import Dialog from '../../Dialog';
import router from 'next/router';
import { useUser } from '../../CacheProvider';

interface QuantityInputProps {
  value?: number;
  unit?: string;
  onChange: (value: number) => void;
  max: number;
  limited?: string;
  disabled?: boolean;
  hasParticipate?: boolean;
}

const Container = styled.div(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    maxWidth: '11rem',
    margin: '0 auto',
    position: 'relative',
  },
}));

const DisplayValue = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.accent[300][0],
  borderRadius: theme.radius.pill,
  fontSize: theme.spacing(2),
  margin: theme.spacing(2, 0),
  padding: theme.spacing(1, 0),
}));

const RotateButton = styled(IconButton)(({ theme }) => ({
  borderRadius: theme.radius.pill,
  padding: '.8em',
  '&.soft-disable': {
    opacity: '50%',
  },
}));

const ButtonsWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
  },
}));

export default function QuantityInput({
  value = 0,
  unit = __t('survey.vote'),
  onChange,
  max,
  limited,
  disabled,
  hasParticipate,
}: QuantityInputProps) {
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { loaded, authorized } = useUser();

  const handleChange: (amount: 1 | -1) => void = (amount) => {
    const newValue = value + amount;
    if (amount === 1 && (newValue > max || limited === 'vote.total_vote_restriction' || limited === 'vote.item_restriction' && value === 0)) {
      setTooltipMessage(__t(limited || 'vote.item_vote_restriction'));
      setShowTooltip(true);
    } else if (newValue >= 0) onChange(newValue);
    if (newValue <= max && newValue >= 0 && !limited) onChange(newValue);
  };

  useEffect(() => {
    if (showTooltip) {
      const tooltipTimeout = setTimeout(() => {
        setShowTooltip(false);
      }, 2000);
      return () => clearTimeout(tooltipTimeout);
    }
  }, [showTooltip]);

  const getDisplayValue = () => {
    if (hasParticipate || value) {
      return `${value} ${unit}`;
    } else if (disabled) {
      return `- ${unit}`;
    }
    return `0 ${unit}`;
  };

  const handleIncrement = () => {
    if (loaded && !authorized) {
      setOpenDialog(true);
    } else {
      handleChange(1);
    }
  };

  const handleDecrement = () => {
    handleChange(-1);
  };

  return (
    <>
      <Container>
        <DisplayValue>
          {getDisplayValue()}
        </DisplayValue>
        {!disabled && (
          <ButtonsWrapper>
            {!hasParticipate && (
              <>
                <RotateButton
                  variant="primaryB"
                  ref={setReferenceElement}
                  onClick={handleIncrement}
                  className={value === max ? 'soft-disable' : ''}
                >
                  <Icon cursor="pointer"><IconPlus /></Icon>
                </RotateButton>

                <RotateButton
                  variant="primaryB"
                  onClick={handleDecrement}
                  disabled={value === 0}
                >
                  <Icon cursor="pointer"><IconMinus /></Icon>
                </RotateButton>
              </>
            )}
            <Tooltip
              open={showTooltip}
              anchor={referenceElement}
              placement="bottom-end"
              type="text"
            >
              {tooltipMessage}
            </Tooltip>
          </ButtonsWrapper>
        )}
      </Container>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <Text variant="body1" align="center" mb={6}>{__t('survey.login_dialog')}</Text>
        <Button
          onClick={() => router.push(`/login?redirect=${router.asPath}`)}
          variant="primaryA"
          fullWidth
        >
          {__t('auth.login')}
        </Button>
      </Dialog>
    </>
  );
}
